/**
 * Auth API — sign-in, sign-up, session validation, sign-out, business switching.
 */

import { api, saveToken, clearToken } from "./client";
import type { AuthResponse, SessionResponse, Organization, TwoStepSetup, TwoStepStatus } from "../types/models";

export interface SignInRequest {
  tenant_id: string;
  username: string;
  password: string;
  device_info: {
    name: string;
    identifier: string;
    type: string;
    platform?: string;
  };
}

export interface SignUpRequest {
  tenant_id?: string;
  business_name: string;
  owner_name: string;
  email: string;
  password: string;
  phone?: string;
}

export interface VerifyPhoneRequest {
  phone: string;
  otp: string;
  device_info?: {
    name: string;
    identifier: string;
    type: string;
    platform?: string;
  };
}

export interface ResendPhoneOTPRequest {
  phone: string;
}

export interface ForgotPasswordRequest {
  tenant_id?: string;
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  otp: string;
  new_password: string;
}

export interface VerifyEmailRequest {
  email: string;
  otp: string;
  device_info?: {
    name: string;
    identifier: string;
    type: string;
    platform?: string;
  };
}

export interface ResendOTPRequest {
  email: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

const generateDeviceId = (): string => {
  const stored = localStorage.getItem('device_id');
  if (stored) return stored;
  
  // Generate a proper UUID v4
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  
  localStorage.setItem('device_id', uuid);
  return uuid;
};

const getDeviceInfo = () => ({
  name: 'Web Browser',
  identifier: generateDeviceId(),
  type: 'web',
  platform: navigator.platform,
});

export const authApi = {
  /**
   * Authenticate with email + password.
   * On success, the token is persisted to localStorage.
   */
  async signIn(username: string, password: string, tenantId: string = ''): Promise<AuthResponse> {
    const requestBody: SignInRequest = {
      tenant_id: tenantId,
      username: username,
      password: password,
      device_info: getDeviceInfo(),
    };
    console.log('Signin request body:', JSON.stringify(requestBody, null, 2));
    const response = await api.post<AuthResponse>("/auth/signin", requestBody);
    // With two-step on there is no token yet, only the second step's.
    if (response.token) saveToken(response.token);
    return response;
  },

  /**
   * Finish a sign-in that needs two-step (#159): the token from the password
   * step and a code from the app, or a recovery code.
   */
  async verifySecondStep(mfaToken: string, answer: { code?: string; recoveryCode?: string }): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/auth/mfa/verify", {
      mfa_token: mfaToken,
      code: answer.code ?? "",
      recovery_code: answer.recoveryCode ?? "",
      device_info: getDeviceInfo(),
    });
    if (response.token) saveToken(response.token);
    return response;
  },

  // The signed-in user's own two-step sign-in (#159).
  getTwoStepStatus(): Promise<TwoStepStatus> {
    return api.get<TwoStepStatus>("/auth/two-step");
  },
  beginTwoStep(): Promise<TwoStepSetup> {
    return api.post<TwoStepSetup>("/auth/two-step/setup", {});
  },
  enableTwoStep(code: string): Promise<{ recovery_codes: string[] }> {
    return api.post("/auth/two-step/enable", { code });
  },
  disableTwoStep(password: string, code: string): Promise<{ success: boolean }> {
    return api.post("/auth/two-step/disable", { password, code });
  },
  regenerateRecoveryCodes(code: string): Promise<{ recovery_codes: string[] }> {
    return api.post("/auth/two-step/recovery-codes", { code });
  },

  /**
   * Register a new organization + owner account.
   * Does not persist token — caller redirects to login after success.
   */
  async signUp(
    businessName: string,
    ownerName: string,
    email: string,
    password: string,
    phone?: string,
    tenantId?: string
  ): Promise<{ success: boolean }> {
    const requestBody: SignUpRequest = {
      tenant_id: tenantId || '',
      business_name: businessName,
      owner_name: ownerName,
      email: email,
      password: password,
      phone: phone || '',
    };
    return api.post("/auth/signup", requestBody);
  },

  /**
   * Request password reset email.
   */
  async forgotPassword(email: string, tenantId?: string): Promise<{ success: boolean }> {
    return api.post("/auth/forgot-password", { tenant_id: tenantId, email: email });
  },

  /**
   * Reset password with OTP code from email.
   */
  async resetPassword(email: string, otp: string, newPassword: string): Promise<{ success: boolean }> {
    return api.post("/auth/reset-password", { email, otp, new_password: newPassword } as ResetPasswordRequest);
  },

  /**
   * Validate the current token and return user + organization info.
   * Throws ApiError (401) when token is invalid or expired.
   */
  getSession(): Promise<SessionResponse> {
    return api.get<SessionResponse>("/auth/session");
  },

  /**
   * Switch to a different business context.
   * Returns a new JWT scoped to the selected business.
   */
  async switchBusiness(businessId: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>("/auth/switch-business", {
      business_id: businessId,
    });
    saveToken(response.token);
    return response;
  },

  /**
   * Invalidate the current session on the server and clear local storage.
   */
  async signOut(): Promise<void> {
    try {
      await api.post("/auth/signout");
    } finally {
      clearToken();
    }
  },

  /**
   * Get list of organizations the user has access to.
   */
  async getOrganizations(): Promise<Organization[]> {
    return api.get<Organization[]>("/auth/organizations");
  },

  /**
   * Verify email with a 6-digit OTP code.
   */
  async verifyEmail(email: string, otp: string): Promise<AuthResponse> {
    return api.post<AuthResponse>("/auth/verify-email", {
      email,
      otp,
      device_info: getDeviceInfo(),
    } as VerifyEmailRequest);
  },

  /**
   * Resend OTP verification code.
   */
  async resendOTP(email: string): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/resend-otp", { email } as ResendOTPRequest);
  },

  async verifyPhone(phone: string, otp: string): Promise<AuthResponse> {
    return api.post<AuthResponse>("/auth/verify-phone", {
      phone,
      otp,
      device_info: getDeviceInfo(),
    } as VerifyPhoneRequest);
  },

  async resendPhoneOTP(phone: string): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/resend-phone-otp", { phone } as ResendPhoneOTPRequest);
  },

  async changePassword(data: ChangePasswordRequest): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/change-password", data);
  },
};
